import ImageConstants from '../constants/ImageConstants';

export const isTextEmpty = (text: string): string => text.trim();

export const capitalize = (string: any): string => {
    if (!string || (string && typeof string !== 'string')) return '';
    return string.replace(/(?:\s|^)[\wÀ-ú]/g, s => `${s.toUpperCase()}` || '');
};

export const formatPresentationString = (
    value: string, maxDigits: number = 2, minDigits: number = 0,
) => {
    const numValue = parseFloat((value));
    return numValue.toLocaleString('en-US', {
        maximumFractionDigits: maxDigits,
        minimumFractionDigits: minDigits,
    });
};

const METRIC_TYPE = {
    currency: {
        symbolLeft: symbol => symbol.concat(' '),
        symbolRight: '',
        maxDigits: 2,
        minDigits: 2,
    },
    percent: {
        symbolLeft: symbol => symbol,
        symbolRight: '%',
        maxDigits: 2,
        minDigits: 0,
    },
    string: {
        symbolLeft: symbol => symbol,
        symbolRight: '',
    },
    default: {
        symbolLeft: symbol => symbol,
        symbolRight: '',
        maxDigits: 2,
        minDigits: 0,
    },
};

const isUndefined = (value: any): boolean => value === undefined;
const isBlankString = (value: any): boolean => value === '';

// eslint-disable-next-line complexity
export const formatValuesForDisplayBasedOnType = (value, type = '', symbol = '') => {
    if (isUndefined(value) || isBlankString(value) || Number.isNaN(value)) return '--';
    if (type === 'string') return value;

    const metricType = METRIC_TYPE[type] || METRIC_TYPE.default;
    const valueFormatted = formatPresentationString(value, metricType.maxDigits,
        metricType.minDigits);

    if (valueFormatted === '∞') return valueFormatted;

    return metricType.symbolLeft(symbol)
        + valueFormatted
        + metricType.symbolRight;
};

export const removeItemFromArray = <T>(array: T[], removedItem: T): T[] => array.filter(
    item => item !== removedItem,
);

export const limitNumberOfCharacters = (string: string, howManyChars: number = 0) => {
    if (string) {
        const dots = string.length > howManyChars ? '...' : '';

        return string.substring(0, howManyChars) + dots;
    }
    return string;
};

export const slugify = (string: string): string => {
    const a = 'àáäâãåèéëêìíïîòóöôùúüûñçßÿœæŕśńṕẃǵǹḿǘẍźḧ·/_,:;';
    const b = 'aaaaaaeeeeiiiioooouuuuncsyoarsnpwgnmuxzh------';
    const p = new RegExp(a.split('').join('|'), 'g');

    return string.toString().toLowerCase()
        .replace(/\s+/g, '-') // Replace spaces with -
        .replace(p, c => b.charAt(a.indexOf(c))) // Replace special characters
        .replace(/&/g, '-and-') // Replace & with 'and'
        // eslint-disable-next-line no-useless-escape
        .replace(/[^\w\-]+/g, '') // Remove all non-word characters
        // eslint-disable-next-line no-useless-escape
        .replace(/\-\-+/g, '-') // Replace multiple - with single -
        .replace(/^-+/, '') // Trim - from start of text
        .replace(/-+$/, ''); // Trim - from end of text
};

export const simpleStringFormater = (string) => {
    const a = 'àáäâãåèéëêìíïîòóöôùúüûñçßÿœæŕśńṕẃǵǹḿǘẍźḧ';
    const b = 'aaaaaaeeeeiiiioooouuuuncsyoarsnpwgnmuxzh';
    const p = new RegExp(a.split('').join('|'), 'g');

    return string.toString().toLowerCase().trim()
        .replace(p, c => b.charAt(a.indexOf(c))); // Replace special characters
};

export const renderIconByContentType = (contentType) => {
    if (contentType === 'google_ads') {
        return ImageConstants.ADS_ACCOUNT.GOOGLE_ADS.SRC;
    }
    if (contentType === 'facebook_ads') {
        return ImageConstants.ADS_ACCOUNT.FACEBOOK_ADS.SRC;
    }
    return '';
};

const URL_PATTERN = new RegExp(
    '(https?:\\/\\/(?:www\\.|(?!www))[a-zA-Z0-9]'
    + '[a-zA-Z0-9-]+[a-zA-Z0-9]\\.[^\\s]{2,}|www\\.[a-zA-Z0-9][a-zA-Z0-9-]'
    + '+[a-zA-Z0-9]\\.[^\\s]{2,}|https?:\\/\\/(?:www\\.|(?!www))[a-zA-Z0-9]'
    + '+\\.[^\\s]{2,}|www\\.[a-zA-Z0-9]+\\.[^\\s]{2,})',
);

export const validateUrl = (str: string): boolean => URL_PATTERN.test(str);

export const renderByTypeofProp = (prop: any): any => {
    if (typeof prop === 'function') {
        return prop();
    }
    return prop;
};

export const formatToSpacedUpperCase = (
    unformattedString: string,
    keepFinalDot: boolean = false,
) => {
    const finalChar = unformattedString?.substr(-1) === '.' && keepFinalDot ? '.' : '';
    const nonAlphaNumericRegex = /[^\d|A-Za-zÀ-ú]/g;
    const formattedString = unformattedString?.toLowerCase()
        .split(nonAlphaNumericRegex)
        .map(capitalize)
        .join(' ');
    return `${formattedString?.trim()}${finalChar}`;
};

export const replaceHyphenOrUnderscore = (unformattedString) => {
    const hyphenOrUnderscoreRegex = /(_|-(?!\s*\d|translate>))/gm;
    const formattedString = unformattedString
        .replace(hyphenOrUnderscoreRegex, ' ');
    return formattedString;
};

export const roundUpWithoutDecimal = (number: number): string => Math.ceil(number).toFixed(0);

/*
 It receives a float with two decimals (3333.33) and then returns (3.333,33)
 */
export const toCurrencyFormat = (value: number): string => {
    const valueWithoutComma = value.toString().replace(/\D/g, '');
    const dotInsertionLength = valueWithoutComma.length - 2;
    const formattedValue = `${valueWithoutComma.substring(0, dotInsertionLength)}.${
        valueWithoutComma.substring(dotInsertionLength)}`;

    const newValue = parseFloat(formattedValue).toFixed(2)
        .replace('.', ',')
        .replace(/(\d)(\d{3})(\d{3}),/g, '$1.$2.$3,')
        .replace(/(\d)(\d{3}),/g, '$1.$2,');

    return newValue;
};

export const currencyToFloat = (value: string): number => parseFloat(value.replace('.', '')
    .replace(',', '.'));

export const snakeCaseToKebabCase = (str: string): string => str.replace(/_/g, '-').toLowerCase();

export const convertAllCapsSnakeCaseToTitleCase = expression => expression
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/\b[a-z](?=[a-z]{2})/g, m => m.toUpperCase());

const unsafeHashReducer = (
    hash: number, charCode: number,
): number => (31 * hash + charCode) % (2 ** 32);

export const hashToNumber = (str: string): number => str.split('').map(char => char.charCodeAt(0))
    .reduce(unsafeHashReducer);

export const orderByName = (firstString, secondString) => {
    const upperFirstName = firstString.toUpperCase();
    const upperSecondName = secondString.toUpperCase();
    if (upperFirstName < upperSecondName) { return -1; }
    if (upperFirstName > upperSecondName) { return 1; }
    return 0;
};

export const replaceWhitespacesWithHyphen = string => string.replace(/\s/, '-');
